import { areaSquareMeters, boxCorners, boxEdges, depthGeometry, distanceMeters, emptyGuides, formatMeters, formatSquareMeters, measurementValue, readGuides, type GuideBox, type Point3, type SpatialGuides } from './spatial-guides-data';

type ScreenPoint = { x: number; y: number; visible: boolean };
type GuideOptions = {
    parent: HTMLElement;
    project: (point: Point3) => ScreenPoint;
    pick: (x: number, y: number) => Promise<Point3 | null>;
    bounds: () => GuideBox | null;
    change: (data: SpatialGuides) => void;
    measuring?: (active: boolean) => void;
    saveHint?: string;
    externalControls?: boolean;
    excludeMeasurement?: (id: string) => boolean;
    showReference?: boolean;
};

// DOM/SVG only: the two applications supply their own world projection/picker.
// Thin screen-space strokes retain the same appearance at every scene scale.
class SpatialGuidesUI {
    private data = emptyGuides();
    private root: HTMLDivElement;
    private svg: SVGSVGElement;
    private panel: HTMLDivElement;
    private list: HTMLDivElement;
    private status: HTMLParagraphElement;
    private surface: HTMLDivElement;
    private draft: Point3[] = [];
    private measuring = false;
    private revision = 0;
    private pickPending = false;
    private boxInputs: HTMLInputElement[][] = [];
    private boxVisible: HTMLInputElement;
    private measurementsVisible: HTMLInputElement;
    private saveMeasurement: HTMLButtonElement;
    private measureButton: HTMLButtonElement;
    private nameInput: HTMLInputElement;
    private drag: { id: string; endpoint: 'start' | 'end'; point: Point3; pointer: number } | null = null;
    private options: GuideOptions;

    constructor(options: GuideOptions) {
        this.options = options;
        const root = this.root = document.createElement('div');
        root.className = 'spatial-guides';
        root.innerHTML = `<style>
            .spatial-guides { position:absolute; inset:0; pointer-events:none; z-index:35; font:12px/1.5 system-ui,sans-serif; color:#eee; }
            .spatial-guides [hidden] { display:none !important; }
            .spatial-guides svg { position:absolute; inset:0; width:100%; height:100%; overflow:hidden; }
            .spatial-guides .guide-line { stroke:#e6ebee; stroke-width:1; opacity:.62; vector-effect:non-scaling-stroke; }
            .spatial-guides .guide-measure { stroke:#48cfcb; stroke-width:1.5; opacity:.95; }
            .spatial-guides .guide-area { fill:#48cfcb; fill-opacity:.2; stroke:none; }
            .spatial-guides .guide-label { fill:#f0f5f8; font-size:12px; paint-order:stroke; stroke:#15191ddd; stroke-width:5; stroke-linejoin:round; text-anchor:middle; }
            .spatial-guides .guide-point { fill:#202a32; stroke:#c8eaff; stroke-width:1.5; }
            .spatial-guides .guide-point.editable { pointer-events:all; cursor:grab; touch-action:none; }
            .spatial-guides button, .spatial-guides input { font:inherit; color:inherit; box-sizing:border-box; }
            .spatial-guides button { background:#30383f; border:1px solid #53616b; border-radius:5px; padding:5px 9px; cursor:pointer; }
            .spatial-guides button:hover { background:#42505b; }
            .spatial-guides button:disabled { opacity:.45; cursor:default; }
            .spatial-guides button[aria-pressed=true] { border-color:#a6dfff; background:#35576d; }
            .spatial-guides .guide-toggle { position:absolute; top:12px; left:12px; pointer-events:auto; }
            .spatial-guides .guide-panel { position:absolute; top:52px; left:12px; width:300px; max-width:calc(100% - 24px); max-height:calc(100% - 135px); overflow:auto; padding:14px; border:1px solid #65747c66; border-radius:9px; background:#171d23f2; box-shadow:0 5px 24px #0005; pointer-events:auto; box-sizing:border-box; }
            .spatial-guides h3 { font-size:13px; margin:0 0 9px; }
            .spatial-guides p { margin:8px 0; color:#bbc6ce; }
            .spatial-guides .guide-row { display:flex; align-items:center; gap:6px; margin:7px 0; }
            .spatial-guides .guide-row > span { min-width:62px; }
            .spatial-guides input[type=number] { width:57px; min-width:0; }
            .spatial-guides input:not([type=checkbox]) { background:#0d1217; border:1px solid #53616b; border-radius:3px; padding:4px; }
            .spatial-guides input[type=text] { width:100%; }
            .spatial-guides .guide-item { display:flex; align-items:center; gap:6px; margin:7px 0; }
            .spatial-guides .guide-item span { flex:1; overflow-wrap:anywhere; }
            .spatial-guides hr { border:0; border-top:1px solid #53616b77; margin:14px 0; }
            .spatial-guides .guide-surface { position:absolute; inset:0; pointer-events:auto; touch-action:none; cursor:crosshair; }
        </style>`;
        this.surface = document.createElement('div');
        this.surface.className = 'guide-surface';
        this.surface.hidden = true;
        root.append(this.surface);
        this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        this.svg.setAttribute('aria-label', 'Measurements and bounding box');
        root.append(this.svg);
        const toggle = this.button('Measurements & box', () => {
            this.panel.hidden = !this.panel.hidden;
            toggle.setAttribute('aria-expanded', String(!this.panel.hidden));
            if (this.panel.hidden) this.setMeasuring(false);
            this.render();
        });
        toggle.className = 'guide-toggle';
        toggle.setAttribute('aria-expanded', 'false');
        if (!options.externalControls) root.append(toggle);
        this.panel = document.createElement('div');
        this.panel.className = 'guide-panel';
        this.panel.hidden = true;
        this.panel.setAttribute('aria-label', 'Measurements and bounding box settings');
        if (!options.externalControls) root.append(this.panel);
        ['pointerdown', 'pointerup', 'click', 'dblclick', 'wheel', 'keydown', 'keyup'].forEach((name) => {
            this.panel.addEventListener(name, event => event.stopPropagation());
        });
        const title = document.createElement('h3');
        title.textContent = 'Measurements';
        this.panel.append(title);
        this.measurementsVisible = this.checkbox('Show measurements', (checked) => {
            this.commit({ ...this.data, measurementsVisible: checked });
        });
        this.measureButton = this.button('New measurement', () => this.setMeasuring(!this.measuring));
        this.panel.append(this.measureButton);
        this.status = document.createElement('p');
        this.status.setAttribute('role', 'status');
        this.status.textContent = 'Place two points on the scene. Saved endpoints can be dragged on the surface.';
        this.panel.append(this.status);
        this.nameInput = document.createElement('input');
        this.nameInput.type = 'text';
        this.nameInput.placeholder = 'Measurement name';
        this.nameInput.setAttribute('aria-label', 'Measurement name');
        this.nameInput.maxLength = 120;
        this.panel.append(this.nameInput);
        this.saveMeasurement = this.button('Save measurement', () => {
            if (this.draft.length !== 2) return;
            this.addMeasurement(this.draft[0], this.draft[1], this.nameInput.value.trim());
            this.setMeasuring(false);
        });
        this.saveMeasurement.disabled = true;
        this.panel.append(this.saveMeasurement);
        this.list = document.createElement('div');
        this.panel.append(this.list, document.createElement('hr'));
        const boxTitle = document.createElement('h3');
        boxTitle.textContent = 'Bounding box';
        this.panel.append(boxTitle);
        this.boxVisible = this.checkbox('Show bounding box', (checked) => {
            if (this.data.boundingBox) this.commit({ ...this.data, boundingBox: { ...this.data.boundingBox, visible: checked } });
        });
        this.panel.append(this.button('Fit box to scene', () => {
            const boundingBox = options.bounds();
            if (boundingBox) this.commit({ ...this.data, boundingBox });
            else this.status.textContent = 'Load a scene before fitting a box.';
        }));
        ['Center (m)', 'Size (m)', 'Rotate (°)'].forEach((label, group) => {
            const row = document.createElement('div');
            row.className = 'guide-row';
            const name = document.createElement('span');
            name.textContent = label;
            row.append(name);
            this.boxInputs.push(['X', 'Y', 'Z'].map((axis) => {
                const input = document.createElement('input');
                input.type = 'number';
                input.step = 'any';
                if (group === 1) input.min = '0.000001';
                input.setAttribute('aria-label', `${label} ${axis}`);
                input.title = `${label} ${axis}`;
                row.append(input);
                return input;
            }));
            this.panel.append(row);
        });
        this.panel.append(this.button('Save box', () => {
            const [center, size, rotation] = this.boxInputs.map(inputs => inputs.map(input => (input.value === '' ? NaN : Number(input.value))) as Point3);
            this.commit({ ...this.data, boundingBox: { center, size, rotation, visible: true } });
        }), this.button('Remove box', () => this.commit({ ...this.data, boundingBox: null })), document.createElement('hr'));
        this.panel.append(this.button('Save guides file', () => {
            const url = URL.createObjectURL(new Blob([JSON.stringify(this.data, null, 2)], { type: 'application/json' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'measurements.json';
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }));
        const file = document.createElement('input');
        file.type = 'file';
        file.accept = '.json,application/json';
        file.hidden = true;
        file.addEventListener('change', async () => {
            try {
                if (file.files?.[0]) this.commit(readGuides(JSON.parse(await file.files[0].text())));
            } catch (error) {
                this.status.textContent = String(error);
            }
            file.value = '';
        });
        this.panel.append(file, this.button('Load guides file', () => file.click()));
        const saveHint = document.createElement('p');
        saveHint.textContent = options.saveHint ?? 'Save a guides file to keep measurements and the bounding box.';
        this.panel.append(saveHint);
        options.parent.append(root);
        this.surface.addEventListener('pointerdown', (event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            this.surface.setPointerCapture(event.pointerId);
        });
        this.surface.addEventListener('pointerup', async (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!this.measuring || this.pickPending || this.draft.length >= 2 || event.button !== 0) return;
            this.pickPending = true;
            const revision = this.revision;
            try {
                const point = await this.pick(event);
                if (revision !== this.revision || !this.measuring) return;
                if (point) this.draft.push(point);
                this.status.textContent = this.draft.length === 2 ? `Distance: ${formatMeters(distanceMeters(this.draft[0], this.draft[1]))}` :
                    point ? 'Place the second point.' : 'No surface found. Choose a point on the scene.';
                this.saveMeasurement.disabled = this.draft.length !== 2;
                this.render();
            } catch (error) {
                this.status.textContent = `Could not pick the surface: ${String(error)}`;
            } finally {
                this.pickPending = false;
            }
        });
        this.svg.addEventListener('pointermove', event => this.moveEndpoint(event));
        this.svg.addEventListener('pointerup', event => this.endDrag(event));
        this.svg.addEventListener('pointercancel', () => {
            this.drag = null; this.revision++; this.render();
        });
        this.setData(emptyGuides());
    }

    private button(text: string, action: () => void) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = text;
        button.addEventListener('pointerdown', event => event.stopPropagation());
        button.addEventListener('click', (event) => {
            event.stopPropagation(); action();
        });
        return button;
    }

    private checkbox(text: string, action: (checked: boolean) => void) {
        const label = document.createElement('label');
        label.className = 'guide-row';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.addEventListener('change', () => action(input.checked));
        label.append(input, document.createTextNode(text));
        this.panel.append(label);
        return input;
    }

    private commit(data: SpatialGuides) {
        try {
            this.options.change(readGuides(data));
            this.status.textContent = this.options.saveHint ?? 'Updated. Save a guides file to keep your changes.';
        } catch (error) {
            this.status.textContent = String(error);
        }
    }

    getData() {
        return readGuides(this.data);
    }

    setOverlayVisible(visible: boolean) {
        this.svg.setAttribute('style', visible ? '' : 'display:none');
        if (!visible) {
            this.cancelMeasurement(); this.drag = null;
        }
    }

    reset(data: SpatialGuides) {
        this.setMeasuring(false); this.drag = null; this.setData(data);
    }

    setData(data: SpatialGuides) {
        this.data = readGuides(data);
        this.drag = null;
        this.revision++;
        this.measurementsVisible.checked = this.data.measurementsVisible;
        this.boxVisible.checked = this.data.boundingBox?.visible ?? false;
        this.boxVisible.disabled = !this.data.boundingBox;
        const box = this.data.boundingBox;
        const values = box ? [box.center, box.size, box.rotation] : [[0, 0, 0], [1, 1, 1], [0, 0, 0]];
        this.boxInputs.forEach((inputs, group) => inputs.forEach((input, axis) => {
            input.value = String(Number(values[group][axis].toPrecision(9)));
        }));
        this.list.replaceChildren();
        this.data.measurements.forEach((measurement) => {
            const row = document.createElement('div');
            row.className = 'guide-item';
            const text = document.createElement('span');
            text.textContent = `${measurement.name}: ${measurementValue(measurement)}`;
            const remove = this.button('×', () => this.commit({ ...this.data, measurements: this.data.measurements.filter(m => m.id !== measurement.id) }));
            remove.setAttribute('aria-label', `Remove ${measurement.name}`);
            row.append(text, remove);
            this.list.append(row);
        });
        this.render();
    }

    addMeasurement(start: Point3, end: Point3, name = '') {
        this.commit({ ...this.data,
            measurementsVisible: true,
            measurements: [...this.data.measurements, {
                id: crypto.randomUUID(), name: name || `Measurement ${this.data.measurements.length + 1}`, start, end
            }] });
    }

    open() {
        this.panel.hidden = false; this.render();
    }

    cancelMeasurement() {
        if (this.measuring) this.setMeasuring(false);
    }

    setEditing(editing: boolean) {
        this.panel.hidden = !editing;
        this.render();
    }

    private setMeasuring(active: boolean) {
        this.measuring = active;
        this.revision++;
        this.draft = [];
        this.surface.hidden = !active;
        this.measureButton.textContent = active ? 'Cancel measurement' : 'New measurement';
        this.measureButton.setAttribute('aria-pressed', String(active));
        this.saveMeasurement.disabled = true;
        this.status.textContent = active ? 'Place the first point on the scene.' : 'Saved endpoints can be dragged on the surface.';
        this.options.measuring?.(active);
        this.render();
    }

    private pick(event: PointerEvent) {
        const bounds = this.root.getBoundingClientRect();
        return this.options.pick((event.clientX - bounds.left) / bounds.width, (event.clientY - bounds.top) / bounds.height);
    }

    private async moveEndpoint(event: PointerEvent) {
        const drag = this.drag;
        if (!drag || this.pickPending || event.pointerId !== drag.pointer) return;
        event.stopPropagation();
        this.pickPending = true;
        const revision = this.revision;
        try {
            const point = await this.pick(event);
            if (point && this.drag === drag && revision === this.revision) {
                drag.point = point;
                this.render();
            }
        } catch (error) {
            this.status.textContent = String(error);
        } finally {
            this.pickPending = false;
        }
    }

    private async endDrag(event: PointerEvent) {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.pointer) return;
        event.stopPropagation();
        const revision = ++this.revision;
        try {
            const point = await this.pick(event);
            if (this.drag !== drag || revision !== this.revision) return;
            this.drag = null;
            this.commit({ ...this.data, measurements: this.data.measurements.map(m => (m.id === drag.id ? { ...m, [drag.endpoint]: point ?? drag.point } : m)) });
        } catch (error) {
            this.status.textContent = String(error);
            this.drag = null;
        }
        this.render();
    }

    render() {
        this.svg.replaceChildren();
        const make = (tag: string, attributes: Record<string, string | number>) => {
            const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
            Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)));
            this.svg.append(element);
            return element;
        };
        const segment = (a: ScreenPoint, b: ScreenPoint, measurement = false) => {
            if (a.visible && b.visible) make('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, class: `guide-line${measurement ? ' guide-measure' : ''}` });
        };
        const label = (a: ScreenPoint, b: ScreenPoint, text: string) => {
            if (a.visible && b.visible) make('text', { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 - 10, class: 'guide-label' }).textContent = text;
        };
        const box = this.data.boundingBox;
        if (box?.visible) {
            const corners = boxCorners(box).map(point => this.options.project(point));
            boxEdges.forEach(([a, b]) => segment(corners[a], corners[b]));
            [0, 1, 2].forEach((axis) => {
                const edges = boxEdges.filter(([a, b, edgeAxis]) => edgeAxis === axis && corners[a].visible && corners[b].visible);
                edges.sort(([a, b], [c, d]) => (corners[c].y + corners[d].y) - (corners[a].y + corners[b].y));
                if (edges.length) label(corners[edges[0][0]], corners[edges[0][1]], formatMeters(box.size[axis]));
            });
        }
        // The editor's reference tool owns reference points and their line.
        // Saved calibration data must not become a persistent measurement overlay.
        const measurements = this.data.measurements.filter((measurement) => {
            if (this.options.excludeMeasurement?.(measurement.id)) return false;
            if (measurement.id === 'scale-reference' && this.options.externalControls) return !!this.options.showReference && this.data.referenceVisible !== false;
            if (!this.data.measurementsVisible) return false;
            if (measurement.type === 'area') return this.data.areaVisible;
            if (measurement.type === 'depth') return this.data.depthVisible;
            return this.data.distanceVisible;
        });
        measurements.forEach((measurement) => {
            if (measurement.type === 'area') {
                const polygon = measurement.points.map(point => this.options.project(point));
                if (polygon.every(point => point.visible)) make('polygon', { points: polygon.map(point => `${point.x},${point.y}`).join(' '), class: 'guide-area' });
                polygon.forEach((point, index) => segment(point, polygon[(index + 1) % polygon.length], true));
                if (polygon.every(point => point.visible)) {
                    const center = polygon.reduce((sum, point) => ({ x: sum.x + point.x / polygon.length, y: sum.y + point.y / polygon.length, visible: true }), { x: 0, y: 0, visible: true });
                    make('text', { x: center.x, y: center.y - 10, class: 'guide-label' }).textContent = `${measurement.name} · ${formatSquareMeters(areaSquareMeters(measurement.points))}`;
                }
                return;
            }
            if (measurement.type === 'depth') {
                const geometry = depthGeometry(measurement);
                if (!geometry) return;
                const plane = measurement.plane.map(point => this.options.project(point));
                if (plane.every(point => point.visible)) make('polygon', { points: plane.map(point => `${point.x},${point.y}`).join(' '), class: 'guide-area' });
                plane.forEach((point, index) => segment(point, plane[(index + 1) % 3]));
                const a = this.options.project(geometry.foot); const b = this.options.project(geometry.end);
                segment(a, b, true);
                if (a.visible) make('circle', { cx: a.x, cy: a.y, r: 5, class: 'guide-point' });
                if (b.visible) make('circle', { cx: b.x, cy: b.y, r: 5, class: 'guide-point' });
                label(a, b, `${measurement.name} · ${formatMeters(geometry.distance)}`);
                return;
            }
            const endpoints = { start: measurement.start, end: measurement.end };
            if (this.drag?.id === measurement.id) endpoints[this.drag.endpoint] = this.drag.point;
            const a = this.options.project(endpoints.start);
            const b = this.options.project(endpoints.end);
            segment(a, b, true);
            label(a, b, `${measurement.name} · ${formatMeters(distanceMeters(endpoints.start, endpoints.end))}`);
            ([['start', a], ['end', b]] as const).forEach(([endpoint, screen]) => {
                if (!screen.visible) return;
                const editable = !this.panel.hidden && !this.measuring && !this.options.externalControls;
                const circle = make('circle', { cx: screen.x, cy: screen.y, r: editable ? 6 : 4, class: `guide-point${editable ? ' editable' : ''}` });
                if (editable) {
                    circle.addEventListener('pointerdown', (event: PointerEvent) => {
                        if (event.button !== 0) return;
                        event.preventDefault();
                        event.stopPropagation();
                        this.drag = { id: measurement.id, endpoint, point: endpoints[endpoint], pointer: event.pointerId };
                        this.svg.setPointerCapture(event.pointerId);
                    });
                }
            });
        });
        const draft = this.draft.map(point => this.options.project(point));
        draft.forEach((point) => {
            if (point.visible) make('circle', { cx: point.x, cy: point.y, r: 5, class: 'guide-point' });
        });
        if (draft.length === 2) {
            segment(draft[0], draft[1], true);
            label(draft[0], draft[1], formatMeters(distanceMeters(this.draft[0], this.draft[1])));
        }
    }
}

export { SpatialGuidesUI };
